import { c as _c } from "react/compiler-runtime";
import { watch } from "@arcgis/core/core/reactiveUtils";
import { isNumber } from "es-toolkit";
import { useEffect, useEffectEvent } from "react";

/**
 * Reports the full camera pose (latitude, longitude, altitude, heading, tilt)
 * whenever the map comes to rest after a pan or zoom. Only works with 3D
 * SceneView instances. Relies on the ArcGIS `stationary` flag so the callback
 * is only invoked once the user stops moving the map, not on every intermediate frame.
 *
 * @param viewRef - Ref to the ArcGIS SceneView instance.
 * @param isViewReady - Whether the ArcGIS view has already been initialized.
 * @param onViewStable - Callback invoked with the current camera pose once the map is stationary.
 * @param consumeSkipNextViewSync - Reads and clears a one-shot flag: when it returns
 *   true, the next stable view is a programmatic initial view (deep-link, restore,
 *   or fit to projects) and must not be reported, so a default load does not write
 *   URL params as if the user navigated.
 */
export function useMapViewSync(viewRef, isViewReady, onViewStable, consumeSkipNextViewSync) {
  const $ = _c(11);
  let t0;
  if ($[0] !== consumeSkipNextViewSync || $[1] !== onViewStable || $[2] !== viewRef) {
    t0 = () => {
      const view = viewRef.current;
      if (!view || !onViewStable || view.type !== "3d") {
        return;
      }
      if (consumeSkipNextViewSync?.()) {
        return;
      }
      const {
        position,
        heading,
        tilt
      } = view.camera;
      const {
        latitude,
        longitude,
        z
      } = position;
      if (!isNumber(latitude) || !isNumber(longitude) || !isNumber(z)) {
        return;
      }
      onViewStable({
        lat: latitude,
        lng: longitude,
        altitude: z,
        heading,
        tilt
      });
    };
    $[0] = consumeSkipNextViewSync;
    $[1] = onViewStable;
    $[2] = viewRef;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const emitStableView = useEffectEvent(t0);
  let t1;
  if ($[4] !== emitStableView || $[5] !== isViewReady || $[6] !== viewRef) {
    t1 = () => {
      const view_0 = viewRef.current;
      if (!isViewReady || !view_0) {
        return;
      }
      const handle = watch(() => view_0.stationary, isStationary => {
        if (isStationary) {
          emitStableView();
        }
      });
      return () => handle.remove();
    };
    $[4] = emitStableView;
    $[5] = isViewReady;
    $[6] = viewRef;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== isViewReady || $[9] !== viewRef) {
    t2 = [isViewReady, viewRef];
    $[8] = isViewReady;
    $[9] = viewRef;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  useEffect(t1, t2);
}